// apiService.ts

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export interface ApiResponse {
  status: number;
  data: any;
  additionalData?: any;
}

export interface ApiError {
  status: number;
  message: string;
}

export async function makeApiCall(
  url: string,
  method: HttpMethod,
  data?: any,
  options?: any,
  signal?: AbortSignal | null
): Promise<ApiResponse> {
  const { isPublicRoute } = options || {};

  const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;
  const token = tokenKey ? localStorage.getItem(tokenKey) : null;
  let body;
  let headers: HeadersInit = {};

  if (data instanceof FormData) {
    body = data;
  } else if (data) {
    body = JSON.stringify(data);
    headers["Content-Type"] = "application/json";
  }

  if (!isPublicRoute) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestOptions: RequestInit = {
    method,
    headers,
    body: ["POST", "PUT", "PATCH"].includes(method) ? body : undefined,
    signal,
  };

  if (
    (method === "GET" || method === "DELETE") &&
    data &&
    typeof data === "object"
  ) {
    const queryString = Object.entries(data)
      .filter(([_, value]) => !!value || typeof value === "boolean")
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    url = `${url}?${queryString}`;
  }

  try {
    const response = await fetch(url, requestOptions);
    const contentType = response.headers.get("content-type");

    let responseData: any;

    if (contentType?.includes("application/json")) {
      responseData = await response.json();
    } else if (contentType?.includes("application/pdf")) {
      responseData = await response.blob();
    } else {
      responseData = await response.text();
    }

    let message;
    if (!response.ok) {
      switch (response.status.toString()) {
        case "400":
          message =
            "Bad Request: The server could not understand the request due to invalid syntax.";
          let err = "";
          let res =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;

          if (res?.errors) {
            Object.keys(res.errors).forEach((key) => {
              res.errors[key].forEach((error: string) => {
                err += `${error}\n`;
              });
            });
          } else if (res?.detail) {
            try {
              let parsedDetail =
                typeof res.detail === "string"
                  ? JSON.parse(res.detail)
                  : res.detail;

              if (parsedDetail?.errors) {
                Object.keys(parsedDetail.errors).forEach((key) => {
                  parsedDetail.errors[key].forEach((error: string) => {
                    err += `${error}\n`;
                  });
                });
              } else {
                err = res.detail;
              }
            } catch (e) {
              console.error("Error parsing detail field:", e);
              err = res.detail?.replace(/\\u0022/g, '"') || message;
            }
          } else if (res?.title) {
            err = res.title;
          }

          message = err ? err : message;
          break;
        case "409":
          message = "Resource already exists";
          var respon =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;
          message = respon.detail ? respon.detail : message;
          break;
        case "401":
          message =
            "your session has expired due to 4 hours of inactivity. Please sign in again to continue";
          window.dispatchEvent(new CustomEvent("session-expired"));
          break;
        case "403":
          message = "Forbidden: you do not have access rights to the content";
          break;
        case "404":
          message =
            "Not Found: The server can not find the requested resource.";
          break;
        case "500":
          message =
            "Internal Server Error: The server encountered an unexpected condition that prevented it from fulfilling the request.";
          break;
        case "502":
          message =
            "Bad Gateway: The server, while acting as a gateway or proxy, received an invalid response from the upstream server.";
          break;
        case "429":
          message =
            "Limit Reached: Please remove existing one before adding a new one.";
          var respon =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;
          message = respon.detail ? respon.detail : message;
          break;

        default:
          message =
            responseData.Detail || "An error occurred. Please try again later.";
      }
      return {
        data: message,
        status: response.status,
        additionalData: responseData?.additionalData,
      };
    }
    return { data: responseData, status: response.status };
  } catch (error) {
    console.error("Error during API call:", error);
    throw new Error("Failed to make API call");
  }
}
